import React, { useState, useEffect, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { MAX_REVIEW_LENTGH } from "constants/CommonConstants";
import { getCourseRatings, deleteCourseRating } from "action/course";
import { courseRatingsList } from "constants/course";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { DEFAULT_RECORDS_PER_PAGE } from "constants/CommonConstants";

const renderStars = (rating) => {
  return (
    <span className="enrolled-stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`${i < Math.round(rating) ? "fas" : "far"} fa-star mr-1 review-feedback-star`}
        />
      ))}
    </span>
  );
};

const ReviewFeedback = ({ courseId, isChannelPartner = false }) => {
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [reviews, setReviews] = useState([]);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const courseRatingsListData = useSelector(
    (state) => state.course.courseRatingsList,
  );

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      !listState.isLoading &&
      pageNum < totalPages
    ) {
      setPageNum((prev) => prev + 1);
    }
  }, [
    listState.isLoading,
    pageNum,
    courseRatingsListData?.data?.totalRecords,
    courseRatingsListData?.data?.noOfRecordsPerPage,
  ]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (courseId) {
      fetchRatings();
    }
  }, [courseId, pageNum]);

  useEffect(() => {
    if (
      !courseRatingsListData ||
      Object.keys(courseRatingsListData).length === 0
    )
      return;

    if (courseRatingsListData?.code === 200) {
      const ratingData = courseRatingsListData?.data;
      const ratingList = ratingData?.TraineeRatings || [];

      setReviews((prev) =>
        pageNum === 1 ? ratingList : [...prev, ...ratingList],
      );

      setListState({
        isLoading: false,
        error: "",
        noRecords: pageNum === 1 && ratingList.length === 0,
      });
    } else if (courseRatingsListData?.code || courseRatingsListData?.message) {
      setListState({
        isLoading: false,
        error:
          courseRatingsListData?.message ||
          "Failed to load reviews! Please try again.",
        noRecords: false,
      });
    }
  }, [courseRatingsListData]);

  const handleDeleteReview = async (reviewId) => {
    if (!reviewId) return;

    try {
      const res = await deleteCourseRating(reviewId);

      if (res?.code === 200) {
        notify("success", res?.message || "Review deleted successfully!");

        fetchRatings();
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to delete review!";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong!");
    }
  };

  const handleDeleteClick = (review) => {
    showAlert({
      title: `Are you sure you want to delete ${review.userFullName}’s review?`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: () => handleDeleteReview(review.id),
    });
  };

  const toggleExpand = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  const fetchRatings = async () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: prev.noRecords && pageNum === 1,
    }));

    if (pageNum === 1) {
      dispatch({ type: courseRatingsList, payload: {} });
    }

    getCourseRatings(dispatch, courseId, pageNum);
  };

  const ratedReviews = reviews.filter((r) => r.rating > 0);
  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => {
    const count = ratedReviews.filter((r) => r.rating === star).length;
    const percent =
      count > 0 ? Math.round((count / ratedReviews.length) * 100) : 0;
    return { star, count, percent };
  });
  const totalPages = Math.ceil(
    (courseRatingsListData?.data?.totalRecords || 0) /
      (courseRatingsListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE),
  );

  return (
    <>
      <div className="review-feedback-tab-content">
        <Row className={isChannelPartner ? "mt-4" : "mt--2"}>
          <Col lg="8">
            <span className="total-records d-block mb-3">
              Total Records: {courseRatingsListData?.data?.totalRecords || 0}
            </span>

            {listState.isLoading && pageNum === 1 ? (
              <div className="loaderContainer-sm">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : listState.error && pageNum === 1 ? (
              <div className="loaderContainer-sm error-style">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation" />
                </div>
                <span>{listState.error}</span>
              </div>
            ) : listState.noRecords ? (
              <div className="loaderContainer-sm empty-style ">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info" />
                </div>
                <span>No reviews yet</span>
              </div>
            ) : (
              <>
                <div className="reviews-scroll-container">
                  {reviews.map((review, index) => {
                    const reviewId = review.ID || review.id || index;
                    const isExpanded = !!expandedReviews[reviewId];
                    const commentStr = review.review || "";
                    const shouldTruncate =
                      commentStr.length > MAX_REVIEW_LENTGH;

                    const displayComment =
                      shouldTruncate && !isExpanded
                        ? `${commentStr.substring(0, MAX_REVIEW_LENTGH)}...`
                        : commentStr;

                    return (
                      <Card
                        key={index}
                        className="content-card review-card mb-3 section-cards"
                      >
                        <CardBody>
                          <div className="d-flex align-items-start justify-content-between mb-2 flex-wrap">
                            <div className="d-flex align-items-center">
                              {review.profileImagePath ? (
                                <img
                                  src={review.profileImagePath}
                                  alt={review.userFullName}
                                  className="review-feedback-avatar review-avatar mr-3 rounded-circle review-feedback-profile-image"
                                  onClick={() =>
                                    setSelectedImage(review.profileImagePath)
                                  }
                                />
                              ) : (
                                <div className="review-feedback-avatar review-avatar mr-3 d-flex align-items-center justify-content-center text-white font-weight-bold rounded-circle">
                                  {review.userFullName
                                    ? review.userFullName.charAt(0)
                                    : "U"}
                                </div>
                              )}
                              <div>
                                <div className="review-feedback-trainee-name font-weight-bold text-dark">
                                  {review.userFullName}{" "}
                                  <span className="text-muted small ml-1">
                                    ({review.uUID})
                                  </span>
                                </div>
                                <div className="text-muted small mb-1">
                                  Batch:{" "}
                                  <span className="text-dark font-weight-600">
                                    {review.batchName}
                                  </span>
                                </div>
                                <div className="small">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center mt-2 mt-sm-0">
                              <span className="text-muted mt-n2 small mr-2">
                                <i className="fa-regular fa-calendar mr-1" />
                                {review.ratingDate}
                              </span>
                              
                              {!isChannelPartner && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 mt-n2 text-danger no-box-shadow-outline"
                                onClick={() => handleDeleteClick(review)}
                                title="Delete Review"
                              >
                                <i className="fa fa-trash" />
                              </Button>
                              )}
                            </div>
                          </div>
                          {commentStr ? (
                            <p className="lesson-description-content review-feedback-comment mb-0 mt-3 text-justify">
                              {displayComment}
                            </p>
                          ) : (
                            <p className="lesson-description-content review-feedback-comment mb-0 mt-3 text-muted">
                              No reviews yet
                            </p>
                          )}
                          {shouldTruncate && (
                            <div className="text-center mt-2 w-100">
                              <button
                                onClick={() => toggleExpand(reviewId)}
                                className="lesson-read-more-btn"
                              >
                                {isExpanded ? "Read Less" : "Read More"}
                              </button>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    );
                  })}

                  {listState.isLoading && pageNum > 1 && (
                    <div className="text-center py-3">
                      <i className="fa fa-spinner fa-spin fa-lg text-muted" />
                    </div>
                  )}

                  {!listState.isLoading &&
                    pageNum >= totalPages &&
                    reviews.length > 0 && (
                      <div className="text-center text-muted py-3 small">
                        No more reviews
                      </div>
                    )}
                </div>
              </>
            )}
          </Col>

          <Col lg="4">
            <span className="total-records d-block mb-3">Overall Summary</span>

            <Card className="content-card section-cards sticky-top review-feedback-summary-card">
              <CardBody className="text-center">
                <div className="display-4 font-weight-bold text-dark mb-1">
                  {courseRatingsListData?.data?.averageCourseRating || 0}
                </div>

                <div className="mb-2">{renderStars(courseRatingsListData?.data?.averageCourseRating || 0)}</div>

                <div className="text-muted mb-4">
                  Based on {courseRatingsListData?.data?.totalRecords || 0}{" "}
                  reviews
                </div>

                {ratingBreakdown.map(({ star, count, percent }) => (
                  <div
                    key={star}
                    className="d-flex align-items-center mb-2 text-left"
                  >
                    <span className="mr-2 review-feedback-star-label">
                      {star} <i className="fas fa-star review-feedback-star" />
                    </span>

                    <div className="flex-grow-1 mx-2 rounded review-feedback-progress-track">
                      <div
                        className="rounded review-feedback-progress-fill"
                        style={{ width: `${percent}%` }}
                      />
                    </div>

                    <span className="text-muted review-feedback-count-label">
                      {count}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {selectedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setSelectedImage(null)}
        >
          <div className="image-modal-content">
            <img src={selectedImage} alt="profile-large" />
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewFeedback;
